import { useEffect, useState } from "react";
import { Mail, BellRing, Send, Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";

export default function AdminEmail() {
  const { t, lang } = useI18n();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [overrideRecipient, setOverrideRecipient] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email/config");
      setConfig(data);
    } catch (_) {
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const body = overrideRecipient.trim() ? { recipient: overrideRecipient.trim() } : {};
      const { data } = await api.post("/admin/email/test", body);
      toast.success(`Email enviado (id: ${(data.id || "").slice(0, 8)}...)`);
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally { setSendingTest(false); }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/email/run-digest", {});
      if (data.skipped) {
        toast.info("No hay celebraciones hoy ni este mes. (Nada que enviar)");
      } else if (data.ok) {
        toast.success(`Resumen mensual enviado (id: ${(data.id || "").slice(0, 8)}...)`);
      } else {
        toast.error(data.error || t.common.error);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || t.common.error;
      toast.error(String(detail));
    } finally { setSendingDigest(false); }
  };

  return (
    <div className="space-y-5" data-testid="admin-email-page">
      <div>
        <h1 className="font-serif-display text-2xl font-semibold">
          {lang === "es" ? "Notificaciones por correo" : "Email notifications"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {lang === "es"
            ? "Resumen mensual de cumpleanos y aniversarios al panel administrativo."
            : "Monthly birthdays and anniversaries digest to admin."}
        </p>
      </div>

      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Mail className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-serif-display text-base font-semibold">
                {lang === "es" ? "Estado de la integracion" : "Integration status"}
              </p>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t.common.loading}</p>
              ) : config?.configured ? (
                <Badge variant="default" className="rounded-full mt-1"><CheckCircle2 className="mr-1 h-3 w-3" />Conectado</Badge>
              ) : (
                <Badge variant="destructive" className="rounded-full mt-1"><AlertTriangle className="mr-1 h-3 w-3" />Sin configurar</Badge>
              )}
            </div>
          </div>

          {config && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <Info label={lang === "es" ? "Remitente" : "From"} value={config.sender} />
              <Info label={lang === "es" ? "Destinatarios" : "Recipients"} value={(config.recipients || []).join(", ") || "—"} />
              <Info label={lang === "es" ? "Hora de envio" : "Send time"} value={`${String(config.send_hour).padStart(2, "0")}:${String(config.send_minute).padStart(2, "0")}  ${config.timezone}`} />
              <Info label={lang === "es" ? "Proximo envio" : "Next run"} value={config.scheduler?.jobs?.[0]?.next_run ? fmtDateTime(config.scheduler.jobs[0].next_run, lang) : "—"} icon={Clock} />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-5 space-y-4">
          <div>
            <p className="font-serif-display text-base font-semibold">
              {lang === "es" ? "Enviar correo de prueba" : "Send test email"}
            </p>
            <p className="text-sm text-muted-foreground">
              {lang === "es"
                ? "Envia el resumen ahora mismo (incluso si no hay celebraciones, lo enviara forzado para probar)."
                : "Sends the digest right now, even if there are no celebrations (forced)."}
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium">{lang === "es" ? "Sobreescribir destinatario (opcional)" : "Override recipient (optional)"}</Label>
            <Input
              placeholder={(config?.recipients || ["correo@ejemplo.com"])[0]}
              value={overrideRecipient}
              onChange={(e) => setOverrideRecipient(e.target.value)}
              data-testid="admin-email-override-input"
              type="email"
              autoComplete="email"
            />
            <p className="text-xs text-muted-foreground">
              {lang === "es"
                ? "En el plan gratuito de Resend solo puedes enviar al correo dueno de la cuenta hasta verificar un dominio."
                : "On Resend's free tier, you can only send to the Resend account-owner email until a domain is verified."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={sendTest} disabled={sendingTest || !config?.configured} data-testid="admin-email-send-test">
              {sendingTest ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
              {lang === "es" ? "Enviar prueba" : "Send test"}
            </Button>
            <Button variant="outline" onClick={sendDigestNow} disabled={sendingDigest || !config?.configured} data-testid="admin-email-send-digest">
              {sendingDigest ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <BellRing className="mr-1 h-4 w-4" />}
              {lang === "es" ? "Enviar resumen de hoy" : "Send today's digest"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Info({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </p>
      <p className="mt-0.5 font-medium break-words">{value}</p>
    </div>
  );
}
